import { Empty, Flex } from "antd";
import { Cell, Pie, PieChart, ResponsiveContainer, Sector } from "recharts";

import { utilityService } from "shared/services";
import { ChartColors } from "shared/constants";
import variables from "../../../_variables.scss";
import { useEffect, useState } from "react";
import {
  IOpenAccountsReceivableStatus,
} from "financials/models";

const renderActiveShape = (props: any) => {
  const RADIAN = Math.PI / 180;
  const {
    cx,
    cy,
    midAngle,
    innerRadius,
    outerRadius,
    startAngle,
    endAngle,
    fill,
    payload,
    percent,
  } = props;
  const sin = Math.sin(-RADIAN * midAngle);
  const cos = Math.cos(-RADIAN * midAngle);
  const sx = cx + (outerRadius + 10) * cos;
  const sy = cy + (outerRadius + 10) * sin;
  const mx = cx + (outerRadius + 30) * cos;
  const my = cy + (outerRadius + 75) * sin;
  const ex = mx + (cos >= 0 ? 1 : -3) * 22;
  const ey = my;
  const textAnchor = cos >= 0 ? "start" : "end";

  return (
    <g>
      <text x={cx} y={cy} dy={8} textAnchor='middle' fill={fill}>
        {utilityService.truncateField(payload.status, 20)}
      </text>
      <Sector
        cx={cx}
        cy={cy}
        innerRadius={innerRadius}
        outerRadius={outerRadius}
        startAngle={startAngle}
        endAngle={endAngle}
        fill={fill}
      />
      <Sector
        cx={cx}
        cy={cy}
        startAngle={startAngle}
        endAngle={endAngle}
        innerRadius={outerRadius + 6}
        outerRadius={outerRadius + 10}
        fill={fill}
      />
      <path
        d={`M${sx},${sy}L${mx},${my}L${ex},${ey}`}
        stroke={fill}
        fill='none'
      />
      <circle cx={ex} cy={ey} r={2} fill={fill} stroke='none' />
      <foreignObject
        x={ex + (cos >= 0 ? 1 : -1) * 12}
        y={ey}
        textAnchor={textAnchor}
        width={100}
        height={variables.fullHeight}
        style={{
          fontSize: variables.smallFontSize,
          color: variables.darkGreyColor,
        }}
        requiredFeatures='http://www.w3.org/TR/SVG11/feature#Extensibility'
      >
        <p
          style={{
            marginBottom: 0,
          }}
        >
          {payload.status} ({payload.totalProjects ?? 0}){" "}
        </p>
        <p
          style={{
            fontSize: variables.smallFontSize,
            color: variables.lightDarkGreyColor,
            marginTop: 0,
          }}
        >{`(${(percent * 100).toFixed(2)}%)`}</p>
      </foreignObject>
    </g>
  );
};

type Props = {
  openAccountsReceivableStatuses: IOpenAccountsReceivableStatus[];
  width?: number | string;
  height?: number | string;
};

export const OpenAccountsReceivableStatuses = ({
  openAccountsReceivableStatuses,
  width = variables.fullWidth,
  height = variables.fullHeight,
}: Props) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const hasData = openAccountsReceivableStatuses?.some(
    (s: IOpenAccountsReceivableStatus) => s.totalProjects > 0
  );

  useEffect(() => {
    const firstWithData = openAccountsReceivableStatuses?.findIndex(
      (s: IOpenAccountsReceivableStatus) => s.totalProjects > 0
    ) ?? 0;
    setActiveIndex(firstWithData >= 0 ? firstWithData : 0);
  }, [openAccountsReceivableStatuses]);

  const onPieEnter = (_: any, index: number) => {
    setActiveIndex(index);
  };

  return (
    <Flex
      vertical
      gap={variables.smallGap}
      style={{ width, height, minHeight: variables.defaultComponentHeight }}
      data-testid='openAccountsReceivableStatuses'
    >
      <h2>Open Accounts Receivable Statuses</h2>
      {hasData ? (
        <ResponsiveContainer
          width={variables.fullWidth}
          height={variables.fullHeight}
        >
          <PieChart>
            <Pie
              activeIndex={activeIndex}
              activeShape={renderActiveShape}
              dataKey='totalProjects'
              nameKey='status'
              data={openAccountsReceivableStatuses}
              cx='50%'
              cy='45%'
              innerRadius={70}
              outerRadius={100}
              onMouseEnter={onPieEnter}
            >
              {openAccountsReceivableStatuses?.map((entry, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={ChartColors[index % ChartColors.length]}
                />
              ))}
            </Pie>
          </PieChart>
        </ResponsiveContainer>
      ) : (
        <Empty />
      )}
    </Flex>
  );
};
